import React from 'react';
import { StatusDot } from '@/components/shared/StatusDot';
import { StrategyGroupComponent } from '@/components/strategies/StrategyGroupComponent';
import { useMarketStatus } from '@/hooks/useMarketStatus';
import type { InfraHealth, StrategyGroup } from '@/types/bot';
import './LeftPanel.css';

interface LeftPanelProps {
  infraHealth: InfraHealth;
  strategyGroups: StrategyGroup[];
  onStartBot: (id: string) => void;
  onStopBot: (id: string) => void;
  isOpen?: boolean;
}

function sortGroups(groups: StrategyGroup[]): StrategyGroup[] {
  return [...groups].sort((a, b) => {
    // Active groups first, then paused, then stopped
    const order = { active: 0, paused: 1, stopped: 2, error: 3 };
    return (order[a.status] ?? 9) - (order[b.status] ?? 9);
  });
}

export const LeftPanel: React.FC<LeftPanelProps> = ({
  infraHealth,
  strategyGroups,
  onStartBot,
  onStopBot,
  isOpen = false,
}) => {
  const latencyPct = Math.min((infraHealth.networkLatencyMs / 100) * 100, 100);
  const latencyColor = infraHealth.syncStatus === 'OK' ? 'green' : infraHealth.syncStatus === 'DEGRADED' ? 'yellow' : 'red';
  const sorted = sortGroups(strategyGroups);
  const { markets } = useMarketStatus();

  return (
    <aside className={`left-panel ${isOpen ? 'left-panel--open' : ''}`}>
      <section>
        <div className="left-panel__section-header">Infrastructure Health</div>
        <div className="infra-health">
          <div className="infra-health__row">
            <StatusDot color={latencyColor} />
            <span className="infra-health__label">Network Latency</span>
            <span className="infra-health__value">{infraHealth.networkLatencyMs.toFixed(2)} ms</span>
          </div>
          <div className="infra-health__bar">
            <div className="infra-health__bar-fill" style={{ width: `${latencyPct}%` }} />
          </div>
        </div>
        {markets.length > 0 && (
          <div className="infra-health__markets">
            {markets.map((m) => {
              const color = m.isOpen === true ? 'green' : m.isOpen === false ? 'red' : 'yellow';
              const session = m.session === 'pre-market' || m.session === 'post-market' ? m.session : m.isOpen ? 'open' : 'closed';
              return (
                <div key={m.exchange} className="infra-health__market-row">
                  <StatusDot color={color} />
                  <span className="infra-health__label">{m.label}</span>
                  <span className="infra-health__value">{session}</span>
                </div>
              );
            })}
          </div>
        )}
      </section>

      <section>
        <div className="left-panel__section-header">Strategies</div>
        {sorted.map((group) => (
          <StrategyGroupComponent
            key={group.name}
            group={group}
            onStartBot={onStartBot}
            onStopBot={onStopBot}
          />
        ))}
      </section>
    </aside>
  );
};
